/**
 * In-memory Control Plane access-token cache (Account Control Plane P5).
 *
 * Two short-lived bearers live here, both IDENTITY_ONLY: the `libra-l3` token
 * the console attaches to L3 API calls, and the administration-audience token
 * it presents to the Control Plane's own operator surface (superadmin grants).
 * They live in memory ONLY — never localStorage — because the durable
 * credential is the HttpOnly CP cookie the browser manages against the CP host.
 * On reload both are re-minted from that cookie via RefreshSession.
 */
export interface CachedL3Token {
  readonly jwt: string
  /** Absolute expiry in epoch milliseconds. */
  readonly expiresAtMs: number
}

/** Refresh proactively this far before expiry so in-flight requests never carry a just-expired token. */
export const L3_TOKEN_REFRESH_SKEW_MS = 30_000

let current: CachedL3Token | null = null
let administration: CachedL3Token | null = null

function expiring(token: CachedL3Token | null, now: number): boolean {
  if (!token) return true
  return token.expiresAtMs - now <= L3_TOKEN_REFRESH_SKEW_MS
}

export function getL3Token(): CachedL3Token | null {
  return current
}

export function setL3Token(token: CachedL3Token): void {
  current = token
}

export function clearL3Token(): void {
  current = null
  administration = null
}

/** True when there is no token, or it is within the refresh skew of expiry. */
export function isL3TokenExpiring(now: number = Date.now()): boolean {
  return expiring(current, now)
}

export function getAdministrationToken(): CachedL3Token | null {
  return administration
}

export function setAdministrationToken(token: CachedL3Token): void {
  administration = token
}

export function isAdministrationTokenExpiring(now: number = Date.now()): boolean {
  return expiring(administration, now)
}
